/**
 * PrivacyPolicyPage - Privacy Policy for BISI Engineering
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/layout/Footer';
import { companyInfo } from '../data/mock';
import { ScrollReveal } from '../components/ui/ScrollReveal';

const PrivacyPolicyPage = () => {
  const lastUpdated = 'February 2025';

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: `Welcome to ${companyInfo.name} ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.`,
    },
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      content: `We may collect information that you voluntarily provide when you:
• Contact us through our contact form (first name, last name, email address, phone number, and message)
• Subscribe to our newsletter or communications
• Request a quote or service information
• Apply for a job or submit an inquiry

We may also automatically collect certain information when you visit our website, such as your IP address, browser type, operating system, referring URLs, pages viewed, and the dates and times of your visits.`,
    },
    {
      id: 'how-we-use',
      title: 'How We Use Your Information',
      content: `We use the information we collect to:
• Respond to your inquiries and provide customer support
• Send you information about our services, projects, and company updates (with your consent)
• Improve our website and user experience
• Comply with legal obligations
• Protect against fraudulent or unauthorized activity

We do not sell, trade, or rent your personal information to third parties.`,
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      content: `Our website may use cookies and similar technologies to enhance your browsing experience. Cookies are small data files stored on your device that help us remember your preferences and understand how you use our site.

You can control cookies through your browser settings. Disabling cookies may affect the functionality of our website.`,
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      content: `We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. When we no longer need your information, we will securely delete or anonymize it.`,
    },
    {
      id: 'data-security',
      title: 'Data Security',
      content: `We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.`,
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      content: `Depending on your location, you may have the right to:
• Access the personal information we hold about you
• Request correction of inaccurate information
• Request deletion of your personal information
• Object to or restrict certain processing of your data
• Withdraw consent where we rely on consent for processing

To exercise these rights, please contact us using the details below.`,
    },
    {
      id: 'third-party',
      title: 'Third-Party Links',
      content: `Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any website you visit.`,
    },
    {
      id: 'children',
      title: 'Children\'s Privacy',
      content: `Our website is not intended for children under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.`,
    },
    {
      id: 'changes',
      title: 'Changes to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.`,
    },
    {
      id: 'contact',
      title: 'Contact Us',
      content: `If you have questions about this Privacy Policy or our data practices, please contact us:

${companyInfo.name}
${companyInfo.address}
Email: ${companyInfo.email}
Phone: ${companyInfo.phone}`,
    },
  ];

  return (
    <div className="min-h-screen bg-[#F5F2ED]">
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="pb-24 pt-24"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#8B7355] text-sm font-medium hover:text-[#7A6348] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <ScrollReveal variant="fadeUp" threshold={0.1}>
            <h1 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] font-semibold mb-2">
              Privacy Policy
            </h1>
            <p className="text-[#6B6B65] text-sm">
              Last updated: {lastUpdated}
            </p>
          </ScrollReveal>

          <div className="mt-12 space-y-12">
            {sections.map((section, index) => (
              <ScrollReveal key={section.id} variant="fadeUp" threshold={0.1} delay={index * 0.03}>
                <section id={section.id}>
                  <h2 className="font-serif text-xl md:text-2xl text-[#1A1A1A] font-semibold mb-4">
                    {section.title}
                  </h2>
                  <div className="text-[#6B6B65] text-sm leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </section>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal variant="fadeUp" threshold={0.1}>
            <div className="mt-16 pt-8 border-t border-[#E0DDD9]">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-[#8B7355] text-sm font-medium hover:text-[#7A6348] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </motion.main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
